import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc, getDoc, collection, getDocs, query, orderBy, writeBatch, serverTimestamp, Timestamp } from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { WatchedItem } from './types';

const env = (window as any)._env_ || {};

const firebaseConfig = {
  apiKey: env.FIREBASE_API_KEY || "no-key",
  authDomain: "the-family-critic.firebaseapp.com",
  projectId: "the-family-critic",
  storageBucket: "the-family-critic.firebasestorage.app",
  messagingSenderId: "880913503470",
  appId: "1:880913503470:web:be58b8431d042a2d8e8fff",
  measurementId: "G-ZNTQQ6W73S"
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

export const analytics = getAnalytics(app);

export const trackTheaterEvent = (eventName: string, params?: object) => {
  logEvent(analytics, eventName, params);
};

/**
 * Resolves with the current user's UID once Firebase Auth is ready.
 * Signs the user in anonymously if they aren't already signed in.
 */
export const authReady: Promise<string> = new Promise((resolve, reject) => {
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      unsubscribe();
      resolve(user.uid);
    }
  });

  signInAnonymously(auth).catch((err) => {
    console.error("Anonymous sign-in failed:", err);
    reject(err);
  });
});

/**
 * One-time migration: copies ratings from users/{username}/ratings
 * to users/{uid}/ratings, and marks the new user doc with migration metadata
 * so we never migrate the same user twice.
 *
 * Safe to call on every login — it's a no-op after the first successful run.
 * Returns true if migration ran (or was already done), false on failure.
 */
export const migrateUserDataIfNeeded = async (username: string): Promise<boolean> => {
  try {
    const uid = await authReady;
    const newUserRef = doc(db, "users", uid);

    // Check whether this UID has already migrated (or is newly created).
    const newUserSnap = await getDoc(newUserRef);
    if (newUserSnap.exists() && newUserSnap.data()?.migratedFrom) {
      // Already migrated for this UID — done.
      return true;
    }

    // Read the legacy ratings under the username-keyed path.
    const legacyRatingsRef = collection(db, "users", username, "ratings");
    const legacyQuery = query(legacyRatingsRef, orderBy("ratedAt", "desc"));
    const legacySnap = await getDocs(legacyQuery);

    if (legacySnap.empty) {
      // Nothing to migrate, but still mark the new doc so we don't keep looking.
      await setDoc(newUserRef, {
        username,
        migratedFrom: username,
        migratedAt: serverTimestamp(),
        lastActive: new Date().toISOString()
      }, { merge: true });
      console.log(`[migration] No legacy data for "${username}"; marked complete.`);
      return true;
    }

    // Copy each rating into the new UID-keyed subcollection. Firestore batches
    // are capped at 500 writes, so we chunk if needed.
    const docs = legacySnap.docs;
    const CHUNK = 450;
    for (let i = 0; i < docs.length; i += CHUNK) {
      const batch = writeBatch(db);
      for (const legacyDoc of docs.slice(i, i + CHUNK)) {
        const data = legacyDoc.data();
        const newRatingRef = doc(db, "users", uid, "ratings", `${uid}_${data.id ?? legacyDoc.id}`);
        batch.set(newRatingRef, data);
      }
      await batch.commit();
    }

    // Mark the user doc as migrated so this never runs again for this UID.
    await setDoc(newUserRef, {
      username,
      migratedFrom: username,
      migratedAt: serverTimestamp(),
      lastActive: new Date().toISOString()
    }, { merge: true });

    console.log(`[migration] Migrated ${docs.length} ratings from "${username}" to uid ${uid}.`);
    return true;
  } catch (e) {
    console.error("[migration] Failed:", e);
    return false;
  }
};

export const fetchHistoryFromCloud = async (_username: string): Promise<WatchedItem[]> => {
  try {
    const uid = await authReady;
    const ratingsRef = collection(db, "users", uid, "ratings");
    const q = query(ratingsRef, orderBy("ratedAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as WatchedItem);
  } catch (e) {
    console.error("Error fetching history:", e);
    return [];
  }
};

export const saveRatingToCloud = async (username: string, movie: any, liked: boolean, source: string = 'recommendation') => {
  try {
    const uid = await authReady;
    const userRef = doc(db, "users", uid);
    const ratingId = `${uid}_${movie.id}`;
    const ratingRef = doc(db, "users", uid, "ratings", ratingId);

    await setDoc(userRef, {
      username,
      lastActive: new Date().toISOString()
    }, { merge: true });

    await setDoc(ratingRef, {
      ...movie,
      liked,
      source,
      ratedAt: serverTimestamp()
    });

    console.log("User initialized and rating saved!");
  } catch (e) {
    console.error("Critical Error:", e);
  }
};
